<script lang="ts" context="module">
	export type ChartTokenItem = {
		id: string;
		label: string;
		color?: string;
		valueLabel?: string;
	};
</script>

<script lang="ts">
	import { createEventDispatcher } from 'svelte';

	export let items: ChartTokenItem[] = [];
	export let selectedItemIds: string[] = [];
	export let mode: 'none' | 'single' | 'toggle' = 'none';
	export let pinFirst = false;
	export let wrap = false;
	export let large = false;
	let className = '';
	export { className as class };

	const dispatch = createEventDispatcher<{ select: { id: string } }>();
</script>

{#if items.length}
	<div
		class="chart-token-list {className}"
		class:chart-token-list-pin-first={pinFirst}
		class:chart-token-list-wrap={wrap}
		class:chart-token-list-large={large}
		role="list"
	>
		{#each items as item (item.id)}
			<svelte:element
				this={mode === 'none' ? 'div' : 'button'}
				type={mode === 'none' ? undefined : 'button'}
				class="chart-token"
				class:chart-token-selected={selectedItemIds.includes(item.id)}
				title={`${item.label}${item.valueLabel ? ` (${item.valueLabel})` : ''}`}
				role={mode === 'none' ? 'listitem' : undefined}
				aria-pressed={mode === 'none' ? undefined : selectedItemIds.includes(item.id)}
				on:click={() => mode !== 'none' && dispatch('select', { id: item.id })}
			>
				{#if item.color}
					<span class="chart-token-dot" style={`background:${item.color}`}></span>
				{/if}
				<span class="chart-token-label">{item.label}</span>
				{#if item.valueLabel}
					<span class="chart-token-value">{item.valueLabel}</span>
				{/if}
			</svelte:element>
		{/each}
	</div>
{/if}

<style>
	.chart-token-list {
		display: flex;
		align-items: center;
		gap: var(--spacing-xs);
		width: 100%;
		min-width: 0;
		overflow-x: auto;
		overflow-y: hidden;
		padding: var(--spacing-xxs) 0;
		overscroll-behavior-inline: contain;
		scrollbar-width: none;
	}

	.chart-token-list::-webkit-scrollbar {
		display: none;
	}

	.chart-token-list-wrap {
		flex-wrap: wrap;
		overflow: visible;
	}

	.chart-token {
		display: inline-flex;
		align-items: center;
		gap: var(--spacing-xs);
		flex: 0 0 auto;
		width: auto;
		max-width: 140px;
		min-width: 0;
		min-height: 28px;
		padding: var(--spacing-xs) var(--spacing-s);
		border: 1px solid rgba(255, 255, 255, 0.12);
		border-radius: var(--radius-pill);
		color: inherit;
		background: color-mix(in srgb, var(--color-2) 76%, transparent);
		font: inherit;
		font-size: 0.75rem;
		line-height: 1.2;
		text-align: left;
		white-space: nowrap;
	}

	button.chart-token {
		cursor: pointer;
	}

	.chart-token-selected {
		border-color: var(--color-primary);
		background: color-mix(in srgb, var(--color-primary) 14%, var(--color-2));
	}

	.chart-token-dot {
		width: 10px;
		height: 10px;
		border-radius: 3px;
		flex: 0 0 auto;
	}

	.chart-token-list-large .chart-token {
		max-width: 180px;
		min-height: 32px;
		padding: var(--spacing-xs) var(--spacing-m);
		font-size: 0.875rem;
	}

	.chart-token-list-large .chart-token-dot {
		width: 12px;
		height: 12px;
	}

	.chart-token-label {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.chart-token-value {
		flex: 0 0 auto;
		color: var(--color-muted);
		font-weight: 600;
	}

	.chart-token-list-pin-first > :first-child {
		position: sticky;
		left: 0;
		z-index: 1;
	}
</style>
